import React, { useEffect, useState } from 'react'
import { withRouter } from 'react-router-dom'
import { withAuth } from '../AuthContext'
import Notification from '../Utils/Notification'
import { SUCCESS } from '../../utils/constants'
import { isValidEmail, shouldTranslateTo } from '../../utils/utils'
import { gaEvents } from '../../analytics/events'
import styled from 'styled-components'
import Error from '../Utils/Error'

import {
  Title,
  Subtitle,
  CustomSubtitle,
  Input,
  ActionButtons,
  LoadingButton,
} from '../Utils/CustomClasses'

const ContainerBox = styled.div`
  padding: 30px 36px 55px 36px;
  background-color: #ffffff;

  @media (max-width: 767px) {
    padding: 25px 25px 25px 25px;
  }
`

const CurrentEmail = styled.div`
  padding: 10px 0 30px;
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`

const FormContent = styled.div`
  width: 40vw;
  min-width: 250px;

  @media screen and (max-width: 768px) {
    width: 100%;
  }

  button {
    width: -webkit-fill-available;
  }
`

const FormContentTop = styled(FormContent)`
  margin: 0 0 90px 0;
`

const BreakSub = styled(Subtitle)`
  display: flex;
  overflow-wrap: anywhere;
`

const ErrorWrapper = styled.div`
  margin: 20px 0 0 0;
`

const Verified = styled.div`
  ${(props) => props.color && `background-color: ${props.color}`};
  border-radius: 30px;
  padding: 0px 12px;
  color: #fff;
  height: 21px;

  display: flex;
  margin: 6px 0 0 7px;

  @media screen and (max-width: 799px) {
    margin: 3px 0 0 7px;
  }
`

const VerifiedImage = styled.img`
  width: 20px;
  height: 20px;
  margin: 1px 3px 0 -2px;
`

const VerifiedSubtitle = styled(Subtitle)`
  color: #fff;
  font-family: 'Open Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  margin: 1px 0 0 0;
`

const EditEmailApp = ({ data, onUpdate }) => {
  const [currentEmail, setCurrentEmail] = useState('')
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [error, setError] = useState('')
  const [pageText, setPageText] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  useEffect(() => {
    gaEvents.eventGeneric(
      'page_load',
      'edit-email-page-loaded',
      data.auth.user.id + ' - edit email - page loaded'
    )

    const language = shouldTranslateTo()
    const getPageText = (pageValue, languageValue) => {
      axios
        .get(`/api/page-text/${pageValue}/${languageValue}`)
        .then((response) => {
          let pageText = response.data.map((textItem) => {
            return textItem.text
          })

          setPageText(pageText)
        })
        .catch((err) => {
          console.log('Error getting page text: ', err)
        })
    }

    setCurrentEmail(data.auth.user.email)
    setEmail(data.auth.user.email)

    getPageText('profile-email', language)
  }, [])

  const handleInputChange = (event) => {
    setError(false)
    setEmailError(false)
    const target = event.target
    setEmail(target.value)
  }

  const handleRequestEmail = (e) => {
    e.preventDefault()
    setIsSending(true)
    axios
      .post('/api/verify-email')
      .then(() => {
        setEmailSent(true)
      })
      .catch(function (error) {
        console.log('Request failure: ', error)
      })
      .finally(() => {
        setIsSending(false)
      })
  }

  const handleUpdateEmail = () => {
    if (!isValidEmail(email)) {
      setEmailError(true)
      setError(pageText[6])
      return
    }

    setIsLoading(true)

    const emailDetails = {
      email: email,
    }

    data.auth
      .updateUser(emailDetails)
      .then(() => {
        Notification({
          type: SUCCESS,
          message: `${pageText[7]}`,
        })

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })

        setCurrentEmail(email)

        setTimeout(function () {
          onUpdate()
        }, 1000)

        setIsLoading(false)
      })
      .catch((e) => {
        Notification({
          data: e,
        })
        setIsLoading(false)
      })
  }

  if (pageText.length === 0) {
    return (
      <div className="card-body text-center">
        <div
          className="spinner-border homestays-spinner"
          style={{ marginTop: '100px' }}
          role="status"
        >
          <span className="sr-only">{pageText[8]}</span>
        </div>
      </div>
    )
  }
  return (
    <ContainerBox>
      <Subtitle size="22px" weight="400">
        {pageText[2]}
      </Subtitle>
      <CurrentEmail>
        {currentEmail ? (
          <>
            <BreakSub size="22px" bold>
              {currentEmail}
            </BreakSub>
            {data.auth.user.emailStatus === 'verified' ? (
              <Verified color="#0da078">
                <VerifiedImage
                  src={'/images/profile/icon-verified-plain.svg'}
                  alt="verified"
                />
                <VerifiedSubtitle
                  color="#fff"
                  size="16px"
                  sizemd="16px"
                  sizesm="16px"
                  margin="4px 0 0 4px"
                  dangerouslySetInnerHTML={{
                    __html: `${pageText[9]}`,
                  }}
                />
              </Verified>
            ) : (
              <Verified color="#e75d66">
                <VerifiedImage
                  src={'/images/profile/icon-not-verified-plain.svg'}
                  alt="verified"
                />
                <VerifiedSubtitle
                  color="#fff"
                  size="16px"
                  sizemd="16px"
                  sizesm="16px"
                  margin="1px 0 0 4px"
                  dangerouslySetInnerHTML={{
                    __html: `${pageText[10]}`,
                  }}
                />
              </Verified>
            )}
          </>
        ) : (
          ''
        )}
      </CurrentEmail>

      {data.auth.user.emailStatus === 'pending' && (
        <>
          <Title
            size="24px"
            sizemd="24px"
            sizesm="24px"
            margin="0 0 10px 0"
            dangerouslySetInnerHTML={{
              __html: `${pageText[11]}`,
            }}
          ></Title>

          <Subtitle
            size="18px"
            weight="400"
            margin="0 0 20px 0"
            dangerouslySetInnerHTML={{
              __html: `${pageText[12]} ${currentEmail}. ${pageText[13]}`,
            }}
          />

          {emailSent && (
            <Subtitle
              color="#ff0000"
              size="18px"
              weight="400"
              margin="0 0 20px 0"
              dangerouslySetInnerHTML={{
                __html: `${pageText[14]}`,
              }}
            />
          )}

          <FormContentTop>
            {isSending ? (
              <LoadingButton padding="10px 10px">{pageText[15]}</LoadingButton>
            ) : (
              <ActionButtons
                type="submit"
                padding="10px 10px"
                onClick={(e) => handleRequestEmail(e)}
              >
                {pageText[16]}
              </ActionButtons>
            )}
          </FormContentTop>
        </>
      )}

      <form
        method="POST"
        action="/email"
        name="myForm"
        aria-label="Edit Email"
        onSubmit={(event) => {
          event.preventDefault()
          handleUpdateEmail()
          return false
        }}
      >
        <input
          type="hidden"
          name="csrf-token"
          value={window.Laravel.csrfToken}
        />

        <FormContent>
          <Title
            size="24px"
            sizemd="24px"
            sizesm="24px"
            margin="0 0 10px 0"
            dangerouslySetInnerHTML={{
              __html: `${pageText[17]}`,
            }}
          ></Title>

          <CustomSubtitle
            bold
            size="16px"
            margin="0 0 11px 0px"
            dangerouslySetInnerHTML={{
              __html: `${pageText[3]}`,
            }}
          />
          <Input
            id="email"
            type="email"
            name="email"
            className={emailError && 'error-border'}
            placeholder={pageText[4]}
            required
            value={email}
            onChange={(e) => handleInputChange(e)}
          />

          {isLoading ? (
            <LoadingButton padding="10px 10px">{pageText[5]}</LoadingButton>
          ) : (
            <ActionButtons type="submit" padding="10px 10px">
              {pageText[5]}
            </ActionButtons>
          )}
        </FormContent>

        <ErrorWrapper>{error && <Error error={error} />} </ErrorWrapper>
      </form>
    </ContainerBox>
  )
}

export default withRouter(withAuth(EditEmailApp))
